import React, { useEffect, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { Close, WarningAmber } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Advisory } from "./advisories";

export interface Props {
  advisories: Advisory[];
  /**
   * When set, the panel sticks at this offset from the viewport top (just
   * below the fixed tab bar) so advisories stay visible while the student
   * scrolls through a long form.
   */
  stickyTop?: string;
}

/**
 * Non-blocking coaching panel for the survey response form (FL-333).
 * Purely informational: submission is never gated on it. Each advisory is
 * individually dismissible; a dismissed advisory stays hidden while its rule
 * keeps firing, but returns if the rule stops firing and later fires again.
 */
export const ResponseAdvisories = ({ advisories, stickyTop }: Props) => {
  const [t] = useTranslation();
  const [dismissed, setDismissed] = useState<string[]>([]);

  // Forget dismissals for advisories that stopped firing, so a later
  // re-occurrence is shown again rather than staying silently suppressed.
  useEffect(() => {
    setDismissed((prev) => {
      const next = prev.filter((id) =>
        advisories.some((advisory) => advisory.id === id),
      );
      return next.length === prev.length ? prev : next;
    });
  }, [advisories]);

  const visible = advisories.filter(
    (advisory) => !dismissed.includes(advisory.id),
  );

  const stickySx =
    stickyTop !== undefined
      ? {
          position: "sticky" as const,
          top: stickyTop,
          zIndex: 2,
          bgcolor: "background.default",
        }
      : {};

  // Positive recognition moved inline (per-answer, same slot as the
  // coaching hints — see InlineCoaching); the panel only ever warns.
  if (visible.length === 0) {
    return null;
  }

  return (
    // Deliberately NOT a MuiAlert: the global Alert override paints warnings
    // as a solid --color-yellow block. Follows the quiet amber idiom from
    // PushToLmsDialog instead.
    <Box
      role="status"
      sx={{
        mx: 3,
        mb: 1,
        p: 1.5,
        display: "flex",
        gap: 1.5,
        borderRadius: 1,
        border: "1px solid",
        borderColor: "warning.main",
        // stickySx (when set) must beat MUI InputLabel's z-index:1; it never
        // geometrically overlaps the fixed tab bar (also zIndex 2) because
        // stickyTop clears it, and is opaque so questions don't show through.
        ...stickySx,
      }}
    >
      <WarningAmber fontSize="small" sx={{ color: "warning.main" }} />
      <Box sx={{ flexGrow: 1 }}>
        <Typography
          variant="body2"
          sx={{ color: "warning.main", fontWeight: 600 }}
        >
          {t("responseCoaching.title")}
        </Typography>
        {visible.map((advisory) => (
          <Box
            key={advisory.id}
            sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ flexGrow: 1, mt: 0.5 }}
            >
              {t(advisory.messageKey)}
            </Typography>
            <IconButton
              size="small"
              aria-label={t("responseCoaching.dismiss")}
              onClick={() => setDismissed((prev) => [...prev, advisory.id])}
            >
              <Close fontSize="inherit" />
            </IconButton>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
